import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { MobileLiveScoreboard } from '../components/match/MobileLiveScoreboard';
import { ArrowLeft } from 'lucide-react';
import { useMobileDetection } from '../hooks/useMobileDetection';

const MobileMatchScoringPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { matches, teams, updateScore, updateMatchStatus, fetchMatches, fetchTeams } = useStore();
  const { orientation } = useMobileDetection();
  
  // Refresh data when component mounts
  useEffect(() => {
    const refreshData = async () => {
      try {
        await fetchMatches();
        await fetchTeams();
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    };
    
    refreshData();
  }, [fetchMatches, fetchTeams]);
  
  const match = matches.find(m => m.id === id);
  
  if (!match) {
    return (
      <div className="text-center py-8 px-4">
        <h2 className="text-xl font-bold mb-4">Match not found</h2>
        <Button onClick={() => navigate('/matches')}>Return to Matches</Button>
      </div>
    );
  }
  
  const team1 = teams.find(t => t.id === match.team1Id);
  const team2 = teams.find(t => t.id === match.team2Id);
  
  // Filter the teams to only include the two teams in the current match
  const currentMatchTeams = teams.filter(t => t.id === match.team1Id || t.id === match.team2Id);
  
  return (
    <div className="pb-20">
      {/* Mobile Header */}
      <div className="sticky top-0 z-20 bg-background border-b safe-area-top">
        <div className="flex items-center justify-between p-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/matches")}
            className="p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex-1 text-center">
            <h1 className="text-lg font-bold truncate">
              {team1?.name} vs {team2?.name}
            </h1>
            <p className="text-xs text-muted-foreground">
              {match.city} • {new Date(match.date).toLocaleDateString()}
            </p>
          </div>
          
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Match Status Card */}
      <div className="p-4">
        <Card className="bg-card">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium">{match.city}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(match.date).toLocaleDateString()}
                </div>
              </div>
              <div className="bg-background text-foreground px-3 py-1 rounded-full text-xs font-semibold border border-input">
                {match.status.toUpperCase()}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Mobile Live Scoreboard */}
      <div className="px-4">
        <MobileLiveScoreboard 
          match={match}
          teams={currentMatchTeams}
          onScoreUpdate={updateScore}
          onMatchStatusChange={updateMatchStatus}
        />
      </div>
    </div>
  );
};

export default MobileMatchScoringPage; 